// @docs:
//   - docs/wip/CREW_PAPER_REQUESTS.md  § Providing and sharing
// ==============================================
// The crew phone flow's look (Crew-1 … Crew-6 artboards): navy page, cards,
// 52 px primary buttons, 44 px secondary ones, labels at 13 px and body at
// 15–17 px. Guardrail 9: nothing here is under 13 px — readability.test.ts
// scans this folder.
// ==============================================

import { forwardRef, useEffect, useRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router-dom';

export const C = {
  page: 'bg-[#061430] text-[#e8eef8]',
  card: 'bg-[#0b1f45] border border-[#1d3a70] rounded-2xl',
  muted: 'text-[#a3b3cf]',
  soft: 'text-[#c7d3e6]',
  link: 'text-[#7cbcff] hover:text-[#b3d8ff] font-semibold underline-offset-2 hover:underline',
  label: 'text-[13px] font-semibold uppercase tracking-[0.14em] text-[#a3b3cf]',
  input: 'w-full min-h-[46px] rounded-[10px] border border-[#2a4a86] bg-[#081a3b] px-3.5 text-[17px] text-white placeholder:text-[#6f82a6] focus:outline-none focus:ring-2 focus:ring-[#5aa9ff]',
  fieldLabel: 'flex flex-col gap-1.5 text-sm font-semibold text-[#c7d3e6]',
  focus: 'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5aa9ff] focus-visible:ring-offset-2 focus-visible:ring-offset-[#061430]',
};

/** The mobile page: 390 px is the target, it widens to a comfortable column on desktop. */
export function CrewShell({ right, children, back }: { right?: ReactNode; children: ReactNode; back?: { to: string; label: string } }) {
  return (
    <div className={`${C.page} min-h-screen`} style={{ minHeight: 'var(--vortex-usable-height, 100vh)' }}>
      <div className="mx-auto flex min-h-screen w-full max-w-[480px] flex-col gap-5 px-5 pb-8 pt-6">
        <header className="flex items-center justify-between gap-3">
          {back ? (
            <Link to={back.to} aria-label={back.label} className={`flex h-11 w-11 items-center justify-center rounded-xl bg-[#0b1f45] ${C.focus}`}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#e8eef8" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 6l-6 6 6 6" /></svg>
            </Link>
          ) : <span className="text-[17px] font-bold">MyPolaris</span>}
          {right && <span className={`text-sm ${C.muted} truncate`}>{right}</span>}
        </header>
        {children}
      </div>
    </div>
  );
}

type Btn = ButtonHTMLAttributes<HTMLButtonElement> & { busy?: boolean };

export const PrimaryButton = forwardRef<HTMLButtonElement, Btn>(function PrimaryButton({ busy, children, className = '', disabled, ...rest }, ref) {
  return (
    <button ref={ref} type="button" {...rest} disabled={disabled || busy} aria-busy={busy || undefined}
      className={`flex min-h-[52px] w-full items-center justify-center rounded-xl bg-[#2563eb] px-4 text-[17px] font-bold text-white hover:bg-[#1d4ed8] disabled:opacity-50 ${C.focus} ${className}`}>
      {children}
    </button>
  );
});

export function SecondaryButton({ children, className = '', tone = 'plain', ...rest }: Btn & { tone?: 'plain' | 'danger' | 'filled' }) {
  const t = tone === 'danger' ? 'border-[rgba(255,107,107,.6)] text-[#ff9a9a] hover:bg-[rgba(255,107,107,.08)]'
    : tone === 'filled' ? 'border-[#2a4a86] bg-[#0f2856] text-white hover:bg-[#16336b]'
    : 'border-[#2a4a86] text-[#e8eef8] hover:bg-[#0f2856]';
  return (
    <button type="button" {...rest} className={`flex min-h-[44px] items-center justify-center rounded-[10px] border px-4 text-[15px] font-semibold disabled:opacity-50 ${t} ${C.focus} ${className}`}>
      {children}
    </button>
  );
}

/** An error the person must see; announced by screen readers. Never swallowed. */
export function ErrorNote({ children, id }: { children: ReactNode; id?: string }) {
  if (!children) return null;
  return <div id={id} role="alert" className="rounded-xl border border-[rgba(255,107,107,.5)] bg-[rgba(255,107,107,.08)] px-3.5 py-3 text-[15px] leading-snug text-[#ffc2c2]">{children}</div>;
}

export function PrivateBanner({ who }: { who: string }) {
  return (
    <div className="flex items-center gap-2.5 rounded-xl bg-[rgba(52,210,122,.1)] px-3.5 py-3 text-[15px] font-semibold text-[#6ee7a2]">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></svg>
      Only {who} and you will see these
    </div>
  );
}

export function Tick({ className = '', color = '#6ee7a2' }: { className?: string; color?: string }) {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={`shrink-0 ${className}`}><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>;
}

export function Chevron() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#7cbcff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="shrink-0"><path d="M9 6l6 6-6 6" /></svg>;
}

/** The page heading, focused when the step changes so screen readers and keyboards follow along. */
export function StepHeading({ children, focusKey, className = '' }: { children: ReactNode; focusKey: string; className?: string }) {
  const ref = useRef<HTMLHeadingElement>(null);
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    ref.current?.focus();
  }, [focusKey]);
  return <h1 ref={ref} tabIndex={-1} className={`m-0 text-[26px] font-bold leading-tight outline-none ${className}`}>{children}</h1>;
}

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return <p role="status" className={`py-10 text-center text-base ${C.muted}`}>{label}</p>;
}

/** A small confirm dialog: Escape and the backdrop cancel, focus starts on the safe choice. */
export function ConfirmDialog({ title, children, confirmLabel, cancelLabel = 'Cancel', onConfirm, onCancel, busy, danger }: {
  title: string; children: ReactNode; confirmLabel: string; cancelLabel?: string; onConfirm: () => void; onCancel: () => void; busy?: boolean; danger?: boolean;
}) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    cancelRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);
  return (
    <div className="fixed inset-0 z-[3000] flex items-end justify-center bg-black/60 p-4 sm:items-center" onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div role="alertdialog" aria-modal="true" aria-labelledby="crew-confirm-title" className={`${C.card} w-full max-w-[440px] space-y-4 p-5 text-[#e8eef8]`}>
        <h2 id="crew-confirm-title" className="m-0 text-xl font-bold">{title}</h2>
        <div className={`text-[15px] leading-relaxed ${C.soft}`}>{children}</div>
        <div className="flex gap-2.5">
          <button ref={cancelRef} type="button" onClick={onCancel} className={`flex min-h-[44px] flex-1 items-center justify-center rounded-[10px] border border-[#2a4a86] px-4 text-[15px] font-semibold ${C.focus}`}>{cancelLabel}</button>
          <button type="button" onClick={onConfirm} disabled={busy} className={`flex min-h-[44px] flex-1 items-center justify-center rounded-[10px] px-4 text-[15px] font-semibold disabled:opacity-50 ${danger ? 'bg-[#b91c1c] text-white' : 'bg-[#2563eb] text-white'} ${C.focus}`}>{busy ? 'Working…' : confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}
